import { AuthenticateStatus } from '@/config';
import { useUserData } from '@/zustand';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import { Box, Button, IconButton, Skeleton, Typography, TypographyProps } from '@mui/material';
import Link from 'next/link';
import { useRouter } from 'next/router';
import usePopup, { PopupUtils } from 'src/logic/hooks/use-popup';
import { sm } from 'src/site-map';
import PrimaryButton from '../../components/buttons/PrimaryButton';

const drawerLinkItemProps: TypographyProps = {
  fontSize: 16,
  fontWeight: 600,
  sx: { cursor: 'pointer', borderRadius: '8px', px: 1, transition: 'all 200ms' },
  color: '#000',
};

const selectedDrawerLinkItemProps: TypographyProps = {
  ...drawerLinkItemProps,
  sx: { ...drawerLinkItemProps.sx, backgroundColor: '#014AA4' },
  color: '#fff',
};

const LandingNavbar = ({ contactUsPopup }: { contactUsPopup: PopupUtils }) => {
  const router = useRouter();
  const isAuthenticated = useUserData(state => state.authenticationStatus === AuthenticateStatus.AUTHENTICATED);
  const isInitializing = useUserData(state => state.isInitializing);
  const menu = usePopup();

  const onContactClick = () => {
    if (router.pathname === sm.contactUs.url) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      contactUsPopup.handleOpen();
    }
  };

  const links = [
    { label: isAuthenticated ? 'Directory' : 'Explore the Solution', href: sm.catalogue.index.url },
    {
      label: 'Careers',
      href: sm.careers.index.url,
      isSelected: !contactUsPopup.isOpen && router.pathname === sm.careers.index.url,
    },
    {
      label: 'Blog',
      href: sm.blog.index.url,
      isSelected: !contactUsPopup.isOpen && router.pathname === sm.blog.index.url,
    },
    {
      label: 'Contact us',
      href: undefined,
      isSelected: contactUsPopup.isOpen || router.pathname === sm.contactUs.url,
      onClick: onContactClick,
    },
  ];

  const authLinks = isInitializing
    ? []
    : isAuthenticated
    ? [
        {
          label: 'Go To Portal',
          href: sm.portals.index.url,
          isSelected: false,
          onClick: undefined,
        },
      ]
    : [
        { label: 'Login', href: sm.login.url, isSelected: false, onClick: undefined },
        { label: 'Get Started', href: sm.register.index.url, isSelected: false, onClick: undefined },
      ];

  return (
    <>
      {/* SEO SSR Link Graph */}
      <nav style={{ display: 'none' }}>
        <a href={sm.home.url}>Home</a>
        <a href={sm.login.url}>Login</a>
        <a href={sm.contactUs.url}>Contact Us</a>
        <a href={sm.register.index.url}>Register</a>
        <a href={sm.catalogue.index.url}>Directory</a>
        <a href={sm.becomeASeller.url}>Become Seller</a>
        <a href={sm.register.asBuyer.index.url}>Become Buyer</a>
      </nav>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 999,
          backgroundColor: 'white',
          backdropFilter: 'blur(44px)',
          boxShadow: '0px 0px 28px 7px #4242421a',

          paddingBlock: {
            xs: '0.85rem',
            sm: '0.85rem',
            md: '0.85rem',
            lg: '1.15rem',
            xl: '1.15rem',
          },
        }}
      >
        {/* Large Screens */}
        <Box
          component={'nav'}
          sx={{
            maxWidth: 'calc(1200px + 3rem)',
            paddingInline: '1.5rem',
            marginInline: 'auto',
            alignItems: 'center',
            justifyContent: 'space-between',
            display: {
              xs: 'none',
              sm: 'none',
              md: 'none',
              lg: 'flex',
              xl: 'flex',
            },
          }}
        >
          <Link href={sm.home.url} passHref>
            <img
              style={{
                objectFit: 'contain',
                height: '40px',
                cursor: 'pointer',
              }}
              src={'/assets/images/logo.png'}
              alt='Logo'
            />
          </Link>

          <Box display={'flex'} gap={3}>
            {links.map(link =>
              link.href ? (
                <Link key={link.label} href={link.href} passHref>
                  <a style={{ textDecoration: 'none', color: 'inherit' }} onClick={link.onClick}>
                    <Typography {...(link.isSelected ? selectedDrawerLinkItemProps : drawerLinkItemProps)}>
                      {link.label}
                    </Typography>
                  </a>
                </Link>
              ) : (
                <Typography
                  key={link.label}
                  {...(link.isSelected ? selectedDrawerLinkItemProps : drawerLinkItemProps)}
                  onClick={link.onClick}
                >
                  {link.label}
                </Typography>
              )
            )}
          </Box>

          <div>
            {isInitializing ? (
              <Skeleton variant='rectangular' width='200px' height={'30px'} sx={{ borderRadius: '8px' }} />
            ) : (
              <>
                {isAuthenticated ? (
                  <Link href={sm.portals.index.url} passHref>
                    <PrimaryButton>Go To Portal</PrimaryButton>
                  </Link>
                ) : (
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <Link href={sm.login.url} passHref>
                      <Button
                        type='button'
                        variant='text'
                        sx={{ color: '#000', fontWeight: 600 }}
                        startIcon={<PersonIcon />}
                      >
                        Log in
                      </Button>
                    </Link>
                    <Link href={sm.register.index.url} passHref>
                      <PrimaryButton>Get Started</PrimaryButton>
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>
        </Box>

        {/* Small Screens */}
        <Box
          component={'nav'}
          sx={{
            maxWidth: 'calc(1200px + 3rem)',
            paddingInline: '1.5rem',
            marginInline: 'auto',
            alignItems: 'center',
            justifyContent: 'space-between',
            display: {
              xs: 'flex',
              sm: 'flex',
              md: 'flex',
              lg: 'none',
              xl: 'none',
            },
          }}
        >
          <Link href={sm.home.url} passHref>
            <img
              style={{
                objectFit: 'contain',
                height: '35px',
                cursor: 'pointer',
              }}
              src={'/assets/images/icon.png'}
              alt='Icon'
            />
          </Link>

          <IconButton type='button' sx={{ color: '#000' }} onClick={menu.handleOpen}>
            <MenuIcon />
          </IconButton>

          <Box
            sx={{
              position: 'fixed',
              width: '100%',
              backgroundColor: 'white',
              top: 0,
              left: 0,
              transition: 'all 0.7s cubic-bezier(0.25, 1, 0.5, 1)',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '1rem',
              pointerEvents: menu.isOpen ? 'auto' : 'none',
              clipPath: menu.isOpen ? 'circle(150% at 100% 0)' : 'circle(0 at 100% 0)',
              display: {
                xs: 'flex',
                sm: 'flex',
                md: 'flex',
                lg: 'none',
                xl: 'none',
              },
            }}
            py={1.75}
            px={3}
            className='min-height-dvh'
          >
            <Box display={'flex'} justifyContent={'space-between'} width={'100%'} alignItems={'center'}>
              <img
                style={{
                  objectFit: 'contain',
                  height: '35px',
                  cursor: 'pointer',
                }}
                src={'/assets/images/icon.png'}
                alt='Icon'
              />
              <IconButton type='button' sx={{ color: '#000' }} onClick={menu.handleClose}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Box display={'flex'} flexDirection={'column'} justifyContent={'center'} alignItems={'center'} gap={2}>
              {[...links, ...authLinks].map(link =>
                link.href ? (
                  <Link key={link.label} href={link.href} passHref>
                    <a
                      style={{ textDecoration: 'none', color: 'inherit' }}
                      onClick={() => {
                        menu.handleClose();
                        // @ts-ignore
                        link.onClick?.();
                      }}
                    >
                      <Typography {...(link.isSelected ? selectedDrawerLinkItemProps : drawerLinkItemProps)}>
                        {link.label}
                      </Typography>
                    </a>
                  </Link>
                ) : (
                  <Typography
                    key={link.label}
                    {...(link.isSelected ? selectedDrawerLinkItemProps : drawerLinkItemProps)}
                    onClick={() => {
                      menu.handleClose();
                      link.onClick?.();
                    }}
                  >
                    {link.label}
                  </Typography>
                )
              )}
            </Box>
            <Typography
              fontSize={{ xs: 11, lg: 14 }}
              fontWeight={400}
              color='#000'
              textAlign={'center'}
              sx={{ opacity: 0.75 }}
            >
              eProcurment © 2025. All Rights Reserved
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default LandingNavbar;
